import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

export const Overline = ({ children, className = "", dark = false }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className="w-6 h-px bg-acid" />
    <span className={`overline ${dark ? "text-white/50" : ""}`}>{children}</span>
  </div>
);

export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className} ${visible ? "fade-up" : "opacity-0"}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export const CTAButton = ({ to, href, children, variant = "primary", className = "", testId, ...rest }) => {
  const styles = {
    primary: "bg-charcoal text-white border border-transparent hover:border-acid",
    accent: "bg-acid text-charcoal hover:bg-acid-dark",
    secondary: "bg-transparent text-ink border border-silver hover:border-charcoal",
    dark: "bg-white text-charcoal border border-transparent hover:bg-acid",
  };
  const cls = `inline-flex items-center gap-2 px-6 py-3.5 text-[13px] font-medium tracking-wide transition-all group ${styles[variant]} ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
    </>
  );
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls} {...rest}>{inner}</a>;
  return <Link to={to} data-testid={testId} className={cls} {...rest}>{inner}</Link>;
};

export const Section = ({ children, className = "", dark = false, id, grid = false }) => (
  <section
    id={id}
    className={`relative ${dark ? "bg-charcoal text-white" : ""} ${className}`}
  >
    {grid && <div className={`absolute inset-0 ${dark ? "grid-lines-dark" : "grid-lines"} pointer-events-none`} />}
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">{children}</div>
  </section>
);
